import type { i18n as I18nInstance } from 'i18next';
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { I18nextProvider } from 'react-i18next';
import { DEFAULT_LOCALE, isLocale, type Locale } from './config';
import { syncDocumentLang } from './html-lang';
import { createI18n } from './i18n';
import { LocaleContext, type LocaleContextValue } from './locale-context';
import { createLocalStorageStore, type LocalePreferenceStore } from './storage';

export interface I18nProviderProps {
  children: ReactNode;
  /**
   * Where the preference is read from and written to. Defaults to
   * `localStorage`; P0-WEB-08 passes one backed by the authenticated user's
   * `identity_users.locale`, and a prerender passes `nullLocaleStore`.
   */
  store?: LocalePreferenceStore | undefined;
  /**
   * Starting locale when the store has nothing. An authenticated shell passes
   * the user's saved locale here so the first paint is already correct.
   */
  initialLocale?: Locale | undefined;
  /** Supply an instance instead of creating one. Tests use this. */
  instance?: I18nInstance | undefined;
}

export function I18nProvider({
  children,
  store,
  initialLocale = DEFAULT_LOCALE,
  instance,
}: I18nProviderProps) {
  // Lazy state rather than a ref: the store is resolved once per mount, and
  // reading it is a plain value read during render rather than a ref access.
  const [resolvedStore] = useState<LocalePreferenceStore>(() => store ?? createLocalStorageStore());

  const [locale, setLocaleState] = useState<Locale>(() => {
    const stored = resolvedStore.read();
    return isLocale(stored) ? stored : initialLocale;
  });

  // Built once. `locale` only seeds it; later changes go through
  // changeLanguage, because a new instance would remount every consumer and
  // discard component state mid-session.
  const [i18n] = useState<I18nInstance>(() => instance ?? createI18n({ locale }));

  useEffect(() => {
    if (i18n.language !== locale) void i18n.changeLanguage(locale);
    syncDocumentLang(locale);
  }, [i18n, locale]);

  const value = useMemo<LocaleContextValue>(
    () => ({
      locale,
      setLocale: (next: Locale) => {
        resolvedStore.write(next);
        setLocaleState(next);
      },
    }),
    [locale, resolvedStore],
  );

  return (
    <I18nextProvider i18n={i18n}>
      <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>
    </I18nextProvider>
  );
}
